import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import AssignmentActionFlowModal, {
	ActionTarget,
	ActionTargetOption,
	ActionSummary
} from '../AssignmentActionFlowModal';
import { pluralLabel } from 'LearningManager/helpers/role';

export type ResetTarget = ActionTarget;

interface ResetCompletionModalProps {
	visible: boolean;
	assignmentId: number;
	scopes: string[];
	tenantId?: number;
	onCancel: () => void;
	onContinue: (target: ResetTarget, userIds?: string[]) => void | Promise<void>;
}

const ResetCompletionModal: React.FC<ResetCompletionModalProps> = ({
	visible,
	assignmentId,
	scopes,
	tenantId,
	onCancel,
	onContinue
}) => {
	const { t } = useTranslation();

	const RESET_OPTIONS: ActionTargetOption[] = useMemo(() => {
		return [
			{
				value: 'all',
				title: t(
					'learning.assignmentActionFlow.reset.options.all.title'
				),
				description: t(
					'learning.assignmentActionFlow.reset.options.all.description'
				)
			},
			{
				value: 'specific',
				title: t(
					'learning.assignmentActionFlow.reset.options.specific.title'
				),
				description: t(
					'learning.assignmentActionFlow.reset.options.specific.description'
				)
			}
		]
	}, [t]);

	return <AssignmentActionFlowModal
		visible={visible}
		assignmentId={assignmentId}
		scopes={scopes}
		tenantId={tenantId}
		audience
		onCancel={onCancel}
		onContinue={onContinue}
		select={{
			title: t('learning.assignmentActionFlow.reset.select.title'),
			description: t(
				'learning.assignmentActionFlow.reset.select.description'
			),
			options: RESET_OPTIONS,
			actionLabel: t(
				'learning.assignmentActionFlow.actions.continue'
			)
		}}
		confirmAll={{
			title: t('learning.assignmentActionFlow.reset.confirmAll.title'),
			intro: t('learning.assignmentActionFlow.reset.confirmAll.intro'),
			items: ({ userCount }) => [
				t(
					pluralLabel('learning.assignmentActionFlow.reset.confirmAll.items', userCount),
					{ count: userCount }
				)
			],
			actionLabel: t('learning.assignmentActionFlow.actions.reset')
		}}
		specific={{
			title: t('learning.assignmentActionFlow.reset.specific.title'),
			description: t(
				'learning.assignmentActionFlow.reset.specific.description'
			),
			actionLabel: t(
				'learning.assignmentActionFlow.actions.continue'
			)
		}}
		confirmSpecific={{
			title: t(
				'learning.assignmentActionFlow.reset.confirmSpecific.title'
			),
			intro: t(
				'learning.assignmentActionFlow.reset.confirmSpecific.intro'
			),
			items: ({ entityCount }) => [
				...(entityCount
					? [
						t(
							pluralLabel('learning.assignmentActionFlow.reset.items.affectSelection', entityCount),
							{ count: entityCount }
						)
					]
					: []),
				t(
					pluralLabel('learning.assignmentActionFlow.reset.items.resetProgress', entityCount),
					{ count: entityCount }
				)
			],
			actionLabel: t('learning.assignmentActionFlow.actions.reset')
		}}
	/>
};

export default ResetCompletionModal;